import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function TransitionsPage(): Node {
  return html`
    <div>
      <h1>Transitions</h1>
      <p><code>Transition</code>, <code>animateEnter</code>, and <code>animateLeave</code> animate elements entering and leaving the DOM.</p>

      <h2>Inline Style Transitions</h2>
      <p>Define enter/leave styles directly — no CSS classes needed:</p>
      ${CodeBlock(`import { createSignal, html } from 'onefold';
import { Transition } from 'onefold/transition';

const view = createSignal('home');

Transition(() => currentView(), {
  duration: 300,
  enterFrom: { opacity: '0', transform: 'translateY(10px)' },
  enterTo:   { opacity: '1', transform: 'translateY(0)' },
  leaveTo:   { opacity: '0', transform: 'translateY(-10px)' },
});`)}

      <h2>CSS Class Transitions</h2>
      ${CodeBlock(`// Use named CSS classes for enter/leave
Transition(() => currentView(), {
  name: 'fade',    // applies .fade-enter, .fade-enter-to, .fade-leave, .fade-leave-to
  duration: 300,
});

/* CSS:
.fade-enter       { opacity: 0; }
.fade-enter-active { transition: opacity 0.3s; }
.fade-leave-active { opacity: 0; transition: opacity 0.3s; }
*/`)}

      <h2>animateEnter / animateLeave</h2>
      <p>Lower-level utilities for custom animation logic on individual elements:</p>
      ${CodeBlock(`import { animateEnter, animateLeave } from 'onefold/transition';

// Animate an element entering
animateEnter(element, {
  name: 'slide',
  duration: 400,
});

// Animate an element leaving, then call done()
animateLeave(element, {
  name: 'slide',
  duration: 400,
}, () => element.remove());`)}

      ${Callout('Transition waits for the leave animation to finish before removing the element. No flicker, no layout jumps.')}

      <h2>Try It</h2>
      <p>Click the buttons to see fade and slide transitions:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { Transition } from 'onefold/transition';

function App() {
  const pages = ['Home', 'About', 'Contact'];
  const current = createSignal(0);

  function currentView() {
    const page = pages[current()];
    const colors = { Home: '#4f46e5', About: '#059669', Contact: '#d97706' };
    return html\`
      <div style=\${{ padding: '24px', background: colors[page], color: 'white', borderRadius: '8px', minHeight: '100px' }}>
        <h3>\${page} Page</h3>
        <p>This content transitions in/out with fade + slide.</p>
      </div>
    \`;
  }

  return html\`
    <div>
      <div style="display:flex;gap:8px;margin-bottom:16px">
        \${pages.map((p, i) => html\`
          <button onclick=\${() => current.set(i)} style=\${() => current() === i ? 'background:#4f46e5;color:white' : ''}>\${p}</button>
        \`)}
      </div>
      \${Transition(
        () => currentView(),
        {
          duration: 300,
          enterFrom: { opacity: '0', transform: 'translateY(10px)' },
          enterTo: { opacity: '1', transform: 'translateY(0)' },
          leaveTo: { opacity: '0', transform: 'translateY(-10px)' },
          mode: 'out-in',
        }
      )}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Transition API — Page Switching with Fade + Slide')}

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { Transition, animateEnter } from 'onefold/transition';

function App() {
  const showA = createSignal(true);

  function ContentA() {
    return html\`
      <div style=\${{ padding: '20px', background: '#dbeafe', borderRadius: '8px', border: '1px solid #93c5fd' }}>
        <h4 style="color:#1e40af">Panel A</h4>
        <p>First panel content. Click swap to transition to Panel B.</p>
      </div>
    \`;
  }

  function ContentB() {
    return html\`
      <div style=\${{ padding: '20px', background: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac' }}>
        <h4 style="color:#166534">Panel B</h4>
        <p>Second panel. The transition uses out-in mode — old leaves before new enters.</p>
      </div>
    \`;
  }

  return html\`
    <div>
      <button onclick=\${() => showA.set(v => !v)}>
        Swap to \${() => showA() ? 'Panel B' : 'Panel A'}
      </button>
      <div style="margin-top:12px;min-height:120px">
        \${Transition(
          () => showA() ? ContentA() : ContentB(),
          {
            duration: 250,
            enterFrom: { opacity: '0', transform: 'scale(0.95)' },
            enterTo: { opacity: '1', transform: 'scale(1)' },
            leaveTo: { opacity: '0', transform: 'scale(0.95)' },
            mode: 'out-in',
          }
        )}
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Transition — Out-In Mode with Scale')}

      <h2>Options</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>name</code></td><td>string</td><td>CSS class prefix (.name-enter, .name-leave, etc.)</td></tr>
        <tr><td><code>duration</code></td><td>number</td><td>Animation duration in ms (default: 300)</td></tr>
        <tr><td><code>enterFrom</code></td><td>CSSStyleDeclaration</td><td>Inline styles at start of enter</td></tr>
        <tr><td><code>enterTo</code></td><td>CSSStyleDeclaration</td><td>Inline styles at end of enter</td></tr>
        <tr><td><code>leaveTo</code></td><td>CSSStyleDeclaration</td><td>Inline styles at end of leave</td></tr>
        <tr><td><code>mode</code></td><td>'default' | 'out-in'</td><td>'out-in' waits for leave before enter</td></tr>
      </table>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/core/templates">Templates</a> — conditional rendering patterns</li>
        <li><a href="/routing/router">Router</a> — page transitions between routes</li>
        <li><a href="/a11y">Accessibility</a> — motion preferences (prefers-reduced-motion)</li>
      </ul>
    </div>
  `;
}
